import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { IProviderProps, IUserDataContext } from "../@types/ContextProviderRelated";
import UserModel from "../models/standard/UserModel";
import { clearStoredAuthToken } from "../utils/authTokenCookieUtils";
import { createUserModelFromKeycloak } from "../utils/keycloakUserUtils";
import { useLoginContext } from "./LoginProvider";

export const userDataDefaultValues: IUserDataContext = {
  // Added an extra "" at the end for the country
  currentUserData: new UserModel("", "", "", "", "", "", "", "", "", "", "", ""),
  setCurrentUserData: () => {},
};

export const UserDataContext = createContext<IUserDataContext>(userDataDefaultValues);

export const useUserDataContext = () => useContext(UserDataContext);

const UserDataProvider = ({ children }: IProviderProps): JSX.Element => {
  const [userData, setUserData] = useState<UserModel>(userDataDefaultValues.currentUserData);
  const { isAuthenticated, isAuthReady, token, tokenParsed } = useLoginContext();
  const keycloakTokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    clearStoredAuthToken();
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;

    if (!isAuthenticated || !token) {
      setUserData((prev) => {
        if (prev.authentificationtoken && prev.authentificationtoken !== keycloakTokenRef.current) {
          return prev;
        }

        return userDataDefaultValues.currentUserData;
      });
      keycloakTokenRef.current = undefined;
      return;
    }

    keycloakTokenRef.current = token;
    setUserData((prev) => createUserModelFromKeycloak(token, tokenParsed, prev));
  }, [isAuthReady, isAuthenticated, token, tokenParsed]);

  const setCurrentUserData = (newUserData: UserModel): void => {
    setUserData(newUserData);
    if (!newUserData.authentificationtoken) {
      clearStoredAuthToken();
    }
  };

  return (
    <UserDataContext.Provider
      value={{
        currentUserData: userData,
        setCurrentUserData,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export default UserDataProvider;
